import {ChevronRightIcon} from '@primer/octicons-react'
import {IconButton, ProgressBar, type ProgressBarProps, VisuallyHidden} from '@primer/react'
import type {MouseEvent, PropsWithChildren, ReactNode} from 'react'
import styles from './MiniMergeSections.module.css'
import {MiniStatusIcon, type MiniStatusIconProps} from './MiniStatusIcon'

function MiniMergeStatusItem({'aria-label': ariaLabel, children}: PropsWithChildren<{'aria-label': string}>) {
  return (
    <section className={styles.MiniStatus} aria-label={ariaLabel}>
      {children}
    </section>
  )
}

function LabelRow({children}: {children: ReactNode}) {
  return <div className={styles.MiniStatusLabelRow}>{children}</div>
}

type LabelProps = PropsWithChildren<{
  icon?: MiniStatusIconProps['type']
  successful?: number
  status?: string
  total?: number
}>

function Label({icon, successful, status, total, children}: LabelProps) {
  const hasRatio = successful !== undefined && total !== undefined
  const ratioText = total === 0 ? '0' : `${successful}/${total}`

  return (
    <span className="d-flex flex-items-center gap-1">
      {icon ? <MiniStatusIcon type={icon} /> : null}
      <strong>{children}</strong>
      {hasRatio ? <span className="fgColor-muted">{ratioText}</span> : null}
      {status ? <VisuallyHidden>{status}</VisuallyHidden> : null}
    </span>
  )
}

type ActionProps = {
  'aria-label': string
  onClick: (event: MouseEvent<HTMLButtonElement>) => void
}

function Action({'aria-label': ariaLabel, onClick}: ActionProps) {
  return (
    <IconButton aria-label={ariaLabel} icon={ChevronRightIcon} onClick={onClick} size="small" variant="invisible" />
  )
}

function Progress({
  'aria-label': ariaLabel,
  successful,
  total,
}: Pick<ProgressBarProps, 'aria-label'> & {successful: number; total: number}) {
  const isEmpty = total === 0

  return (
    <ProgressBar
      aria-label={ariaLabel}
      aria-valuenow={isEmpty ? 0 : undefined}
      bg={isEmpty ? 'neutral.emphasis' : undefined}
      className={styles.MiniStatusProgress}
      progress={isEmpty ? 100 : (successful / total) * 100}
    />
  )
}

MiniMergeStatusItem.LabelRow = LabelRow
MiniMergeStatusItem.Label = Label
MiniMergeStatusItem.Action = Action
MiniMergeStatusItem.Progress = Progress

export {MiniMergeStatusItem}
